import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Chip, Stack, Typography } from '@mui/material';
import ReceivablesImportWizard from './ReceivablesImportWizard';
import { getReceivablesCapabilities } from '../api/receivablesApi';
import { publish } from '../events';

// Keep every account's reporting date and currency visible. No portfolio FX
// conversion or aggregation across different reporting dates is implied.
export default function ReceivablesWorkspace({ tenantId, rows = [], onCommitted }) {
  const [scope, setScope] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let active = true;
    setOpen(false);
    setScope(null);
    if (tenantId) getReceivablesCapabilities(tenantId)
      .then(capabilities => { if (active) setScope({ tenantId, capabilities }); })
      .catch(() => { if (active) setScope({ tenantId, error: true }); });
    return () => { active = false; };
  }, [tenantId]);
  const capabilities = scope?.tenantId === tenantId ? scope.capabilities : null;
  const imported = rows.filter(row => row.receivables);
  if (!capabilities?.enabled && !imported.length) return null;
  const pending = imported.filter(row => row.receivables.assessment?.status !== 'available').length;
  const dates = [...new Set(imported.map(row => row.receivables.asOfDate).filter(Boolean))].sort();
  const currencies = [...new Set(imported.flatMap(row => row.receivables.balancesByCurrency || []).map(balance => balance.currency).filter(Boolean))].sort();
  const committed = result => {
    publish('ar_dashboard_refresh', { tenantId, importId: result?.importId });
    publish('ar_import_started', { tenantId, importId: result?.importId });
    onCommitted?.(result);
  };
  return <Card variant="outlined" sx={{ mb: 3, p: 2.5, borderRadius: 3, borderColor: '#dce4ef' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
      <Box><Typography variant="h6" sx={{ color: '#223650', fontWeight: 600 }}>Receivables workspace</Typography>
        <Typography variant="body2" color="text.secondary">Add invoices, import payments or company aging reports, and review payment allocations.</Typography></Box>
      <Button variant="contained" disabled={!capabilities?.canWrite} onClick={() => setOpen(true)}>Add or import receivables</Button>
    </Stack>
    {imported.length > 0 && <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
      <Chip label={`${imported.length} imported accounts`} /><Chip label={`${pending} assessments need attention`} />
      {currencies.map(currency => <Chip key={currency} label={currency} variant="outlined" />)}
    </Stack>}
    {imported.length > 0 && <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
      Balances and aging are shown by currency in the table. Reporting dates: {dates.length ? dates.join(', ') : 'needs review'}.
      {' '}Unallocated payments remain separate. Review assessment needs in each company’s existing assessment workspace; imports do not start paid checks.
    </Typography>}
    {capabilities?.enabled && !capabilities.canWrite && <Alert severity="info" sx={{ mt: 2 }}>Receivables import requires AR access and permission to edit financial data.</Alert>}
    {scope?.error && imported.length > 0 && <Alert severity="warning" sx={{ mt: 2 }}>Import access could not be verified. Refresh the page to try again.</Alert>}
    {capabilities?.canWrite && <ReceivablesImportWizard key={tenantId} open={open} onClose={() => setOpen(false)} tenantId={tenantId} onCommitted={committed} />}
  </Card>;
}
